// src/App.js

import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Link as MuiLink } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { MotionConfig } from 'framer-motion';
import { ProfilePictureProvider } from './contexts/ProfilePictureContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/common/ToastNotification';
import { useResponsive } from './utils/responsive';
import theme from './theme';
import { colors as brandColors } from './theme/tokens';
import logoImage from './FightDeckWhite.png';
import './styles/fonts.css';
import './utils/setupFounder';
import { useMobileScrollToTop } from './hooks/useScrollToTop';
import usePresenceHeartbeat from './hooks/usePresenceHeartbeat';

// Import services
import analyticsService from './services/analyticsService';
import securityService from './services/securityService';
import systemLogger from './services/systemLogger';

// Lightweight components that can be imported normally
import ErrorBoundary from './components/common/ErrorBoundary';
import ReconnectBanner from './components/common/ReconnectBanner';
import BuildIdentity from './components/common/BuildIdentity';
import PrivateRoute from './components/auth/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ProfileCompletionGuard from './components/auth/ProfileCompletionGuard';
import { shouldRouteToOnboarding } from './features/onboarding/onboardingService';
import OnboardingBanner from './features/onboarding/OnboardingBanner';

// Lazy load ALL heavy components
const CommandPalette = lazy(() => import('./components/common/CommandPalette'));
const Sidebar = lazy(() => import('./components/layout/Sidebar'));
const TopBar = lazy(() => import('./components/topBar/TopBar'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/registration/Register'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const UserDiscoveryPage = lazy(() => import('./components/userDiscovery/UserDiscoveryPage'));
const AvailableListingsPage = lazy(() => import('./components/marketplace/AvailableListingsPage'));
const ListingDetailPage = lazy(() => import('./components/marketplace/ListingDetailPage'));
const SellerDashboard = lazy(() => import('./components/marketplace/SellerDashboard'));
const MyListingsPage = lazy(() => import('./components/marketplace/MyListingsPage'));
const EnhancedUserProfile = lazy(() => import('./components/profile/EnhancedUserProfile'));
const ProfileCompletionFlow = lazy(() => import('./components/profile/ProfileCompletionFlow'));
const BrowseCardsByFighter = lazy(() => import('./components/browseFighter/BrowseCardsByFighter'));
const FighterDetailPage = lazy(() => import('./components/fighterDetails/FighterDetailPage'));
const Logout = lazy(() => import('./components/auth/Logout'));
const MyCollection = lazy(() => import('./components/myCollection/myCollection'));
const CardDetailPage = lazy(() => import('./components/cardDetails/CardDetailPage'));
const ViewSetsComponent = lazy(() => import('./components/viewSets/ViewSets'));
const SetDetails = lazy(() => import('./components/setDetails/SetDetails'));
const LandingPageSimple = lazy(() => import('./components/landing/LandingPageSimple'));
const WishlistPage = lazy(() => import('./components/wishlist/WishlistPage'));
const FavoritesPage = lazy(() => import('./components/favorites/FavoritesPage'));
const AchievementsPage = lazy(() => import('./components/achievements/AchievementsPage'));
const RainbowTracker = lazy(() => import('./components/rainbowTracker/RainbowTracker'));
const MessagesPage = lazy(() => import('./components/messages/MessagesPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const OnboardingFlow = lazy(() => import('./features/onboarding/OnboardingFlow'));
// DEV-ONLY icon review surface.
const DevIcons = lazy(() => import('./pages/DevIcons'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PrivacySettingsPage = lazy(() => import('./pages/PrivacySettingsPage'));

// Admin components - only load when needed
const AddCardPage = lazy(() => import('./pages/AddCardPage'));
const EditCardsPage = lazy(() => import('./pages/EditCardsPage'));
const EditFightersPage = lazy(() => import('./pages/EditFightersPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AddSetPage = lazy(() => import('./pages/AddSetPage'));
const EditSetsPage = lazy(() => import('./pages/EditSetsPage'));
const ProfileSettingsPage = lazy(() => import('./pages/ProfileSettingsPage'));
const SellCard = lazy(() => import('./components/marketplace/SellCard'));
const UserSearchPage = lazy(() => import('./components/userSearch/UserSearchPage'));
const AddFighterForm = lazy(() => import('./components/admin/AddFighterForm'));
const FighterImageAdminPage = lazy(() => import('./pages/FighterImageAdminPage'));
const CardImageAdminPage = lazy(() => import('./pages/CardImageAdminPage'));
const ImageApprovalsPage = lazy(() => import('./pages/ImageApprovalsPage'));
const BulkCardManagerPage = lazy(() => import('./pages/BulkCardManagerPage'));
const BulkCardUpdaterPage = lazy(() => import('./pages/BulkCardUpdaterPage'));
const BulkSetManagerPage = lazy(() => import('./pages/BulkSetManagerPage'));
const IconsAdminPage = lazy(() => import('./pages/IconsAdminPage'));
// TEMP: bulk TCDB image import — delete with src/pages/BulkImageImportPage.js when the catalog is filled
const BulkImageImportPage = lazy(() => import('./pages/BulkImageImportPage'));
// TEMP: backfill missing base-set parallel cards — delete with src/pages/BackfillParallelsPage.js
const BackfillParallelsPage = lazy(() => import('./pages/BackfillParallelsPage'));
// TEMP: ingest the 2010 Leaf MMA base set — delete with src/pages/LeafMmaImportPage.js + leafMmaBaseCatalog.json
const LeafMmaImportPage = lazy(() => import('./pages/LeafMmaImportPage'));
// TEMP: import 2010 Leaf MMA scans (matched by tcdbCid) — delete with src/pages/LeafMmaImagesPage.js
const LeafMmaImagesPage = lazy(() => import('./pages/LeafMmaImagesPage'));
// TEMP: ingest 2010 Leaf MMA autograph insert sets — delete with src/pages/LeafMmaInsertsPage.js + leafMmaInsertsCatalog.json
const LeafMmaInsertsPage = lazy(() => import('./pages/LeafMmaInsertsPage'));
// TEMP: clean up fabricated Round 1 autographs + phantom Blue Ink — delete with src/pages/CleanR1AutographsPage.js
const CleanR1AutographsPage = lazy(() => import('./pages/CleanR1AutographsPage'));
// TEMP: rebuild Round 1 autographs to the confirmed 38 base + 37 red checklist — delete with src/pages/FixR1AutographsPage.js
const FixR1AutographsPage = lazy(() => import('./pages/FixR1AutographsPage'));
// TEMP: repair Round 1 cross-wired parallels — delete with src/pages/FixR1ParallelsPage.js
const FixR1ParallelsPage = lazy(() => import('./pages/FixR1ParallelsPage'));
// PERMANENT: set data integrity audit — run after any import or bulk edit
const AuditSetsPage = lazy(() => import('./pages/AuditSetsPage'));
const FighterManagerPage = lazy(() => import('./pages/FighterManagerPage'));
// TEMP: reconcile legacy card-doc schemes per set — delete with src/pages/ReconcileSetPage.js
const ReconcileSetPage = lazy(() => import('./pages/ReconcileSetPage'));
// TEMP: import scraped TCDB sets — delete with src/pages/ImportScrapedSetPage.js + import-staging/
const ImportScrapedSetPage = lazy(() => import('./pages/ImportScrapedSetPage'));
// TEMP: import scraped-set scans — delete with src/pages/ImportScrapedImagesPage.js
const ImportScrapedImagesPage = lazy(() => import('./pages/ImportScrapedImagesPage'));
// TEMP: normalize existing landscape card images — delete with src/pages/NormalizeOrientationPage.js
const NormalizeOrientationPage = lazy(() => import('./pages/NormalizeOrientationPage'));
// TEMP: reconcile the two card-ID schemes into one — delete with src/pages/ReconcileCardsPage.js
const ReconcileCardsPage = lazy(() => import('./pages/ReconcileCardsPage'));
// Full-viewport branded pulse — reserved for auth / cold start only
const PageLoader = () => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: 'background.default',
    '@keyframes vaultPulse': {
      '0%, 100%': { opacity: 0.3 },
      '50%': { opacity: 1 },
    },
  }}>
    <Box
      component="img"
      src={logoImage}
      alt="FightDecks"
      sx={{
        width: 240,
        maxWidth: '70%',
        height: 'auto',
        animation: 'vaultPulse 1.6s ease-in-out infinite',
      }}
    />
    <Box
      component="span"
      sx={{
        fontFamily: '"Manrope", sans-serif',
        fontWeight: 600,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.3em',
        color: '#D4AF37',
        animation: 'vaultPulse 1.6s ease-in-out infinite',
      }}
    >
      Opening Vault
    </Box>
  </Box>
);

/** In-chrome route skeleton — avoids full-screen PageLoader on internal nav */
const RouteSkeleton = ({ variant = 'panel' }) => {
  const bar = (w, h = 14, mb = 1.5) => (
    <Box
      sx={{
        width: w,
        height: h,
        mb,
        bgcolor: 'rgba(212,175,55,0.1)',
        borderRadius: '2px',
      }}
    />
  );
  const tile = (key) => (
    <Box
      key={key}
      sx={{
        border: `1px solid ${brandColors.border}`,
        borderRadius: '2px',
        bgcolor: brandColors.panel || 'rgba(255,255,255,0.03)',
        minHeight: variant === 'grid' ? 180 : 72,
        p: 2,
      }}
    >
      {bar('40%', 10, 1)}
      {bar('70%', 18, 0)}
    </Box>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: '40vh',
        '@keyframes routePulse': {
          '0%, 100%': { opacity: 0.45 },
          '50%': { opacity: 0.85 },
        },
        animation: 'routePulse 1.4s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
      aria-busy
      aria-label="Loading"
    >
      {bar('28%', 28, 1)}
      {bar('48%', 12, 3)}
      {variant === 'grid' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 2,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => tile(i))}
        </Box>
      ) : variant === 'profile' ? (
        <Box>
          <Box
            sx={{
              height: { xs: 140, md: 200 },
              mb: 2,
              border: `1px solid ${brandColors.border}`,
              borderRadius: '2px',
              bgcolor: 'rgba(212,175,55,0.06)',
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 96,
                  height: 56,
                  border: `1px solid ${brandColors.border}`,
                  borderRadius: '2px',
                  bgcolor: 'rgba(255,255,255,0.03)',
                }}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {Array.from({ length: 5 }).map((_, i) => tile(i))}
        </Box>
      )}
    </Box>
  );
};

const routeSkeletonVariant = (pathname = '') => {
  if (
    pathname.startsWith('/sets') ||
    pathname.startsWith('/my-collection') ||
    pathname.startsWith('/collection') ||
    pathname.startsWith('/fighters') ||
    pathname.startsWith('/fighter') ||
    pathname.startsWith('/browse-fighters') ||
    pathname.startsWith('/marketplace')
  ) {
    return 'grid';
  }
  if (
    pathname.startsWith('/me') ||
    pathname.startsWith('/profile') ||
    pathname.startsWith('/users/') ||
    pathname.startsWith('/u/')
  ) {
    return 'profile';
  }
  if (
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/forgot-password') ||
    pathname === '/'
  ) {
    return 'full';
  }
  return 'panel';
};

const RouteAwareFallback = () => {
  const { pathname } = useLocation();
  const variant = routeSkeletonVariant(pathname);
  if (variant === 'full') return <PageLoader />;
  return <RouteSkeleton variant={variant} />;
};

/** Desktop rail placeholder — avoids full-viewport PageLoader blocking main content */
const SidebarRailSkeleton = () => (
  <Box
    sx={{
      width: 240,
      height: '100%',
      boxSizing: 'border-box',
      backgroundColor: brandColors.surface || '#121315',
      borderRight: `1px solid ${brandColors.border}`,
      px: 3,
      pt: 3,
      pb: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      '@keyframes railPulse': {
        '0%, 100%': { opacity: 0.35 },
        '50%': { opacity: 0.7 },
      },
      '& > *': {
        animation: 'railPulse 1.4s ease-in-out infinite',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '& > *': { animation: 'none' },
      },
    }}
    aria-hidden
  >
    <Box sx={{ width: 168, height: 26, bgcolor: 'rgba(212,175,55,0.14)', borderRadius: '2px', mb: 0.5 }} />
    <Box sx={{ width: 96, height: 10, bgcolor: 'rgba(212,175,55,0.1)', borderRadius: '2px', mb: 2 }} />
    {Array.from({ length: 10 }).map((_, i) => (
      <Box
        key={i}
        sx={{
          height: 40,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: `1px solid ${brandColors.border}`,
          borderRadius: '2px',
        }}
      />
    ))}
  </Box>
);

// Routes that must never render the app chrome (sidebar / top bar)
const AUTH_ROUTES = ['/login', '/register', '/forgot-password'];
const LEGAL_STANDALONE_ROUTES = ['/privacy', '/terms', '/support'];
const CHROME_FREE_ROUTES = [
  ...AUTH_ROUTES,
  ...LEGAL_STANDALONE_ROUTES,
  '/complete-profile',
  '/welcome',
  '/dev/icons',
];
// Onboarding gate never fires on these (auth flow, the flow itself, admin test).
const ONBOARDING_EXEMPT = [...CHROME_FREE_ROUTES, '/logout'];

const ROUTE_TITLES = {
  '/': 'FightDecks',
  '/dashboard': 'Dashboard',
  '/sets': 'Sets',
  '/my-collection': 'My Collection',
  '/browse-fighters': 'Browse Fighters',
  '/marketplace': 'Marketplace',
  '/messages': 'Messages',
  '/wishlist': 'Wishlist',
  '/favorites': 'Favorites',
  '/achievements': 'Achievements',
  '/rainbow-tracker': 'Rainbow Tracker',
  '/notifications': 'Notifications',
  '/me': 'My Profile',
  '/profile': 'Account Settings',
  '/settings/privacy': 'Privacy Settings',
  '/users': 'Discover Collectors',
  '/users/search': 'Find Collectors',
  '/login': 'Sign In',
  '/register': 'Register',
  '/forgot-password': 'Forgot Password',
  '/support': 'Support',
  '/privacy': 'Privacy',
  '/terms': 'Terms',
  '/welcome': 'Welcome',
  '/admin': 'Admin',
};

function documentTitleForPath(pathname) {
  if (ROUTE_TITLES[pathname]) {
    return pathname === '/' ? ROUTE_TITLES[pathname] : `${ROUTE_TITLES[pathname]} · FightDecks`;
  }
  if (pathname.startsWith('/sets/') && pathname.includes('/cards/')) return 'Card · FightDecks';
  if (pathname.startsWith('/sets/')) return 'Set · FightDecks';
  if (pathname.startsWith('/fighters/')) return 'Fighter · FightDecks';
  if (pathname.startsWith('/browse-fighters')) return 'Browse Fighters · FightDecks';
  if (pathname.startsWith('/marketplace/') || pathname.startsWith('/my-listings')) {
    return 'Listing · FightDecks';
  }
  if (pathname.startsWith('/profile/') || pathname.startsWith('/user/')) {
    return 'Collector · FightDecks';
  }
  if (pathname.startsWith('/users/') || pathname.startsWith('/u/')) return 'Collector · FightDecks';
  if (pathname.startsWith('/admin')) return 'Admin · FightDecks';
  if (pathname === '/complete-profile') return 'Complete Profile · FightDecks';
  if (pathname === '/welcome') return 'Welcome · FightDecks';
  return 'FightDecks';
}

const AppContent = () => {
  const { currentUser, userProfile, profileCheckLoading } = useAuth();
  const { isDesktop } = useResponsive();
  const location = useLocation();
  const mainRef = useRef(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Firestore presence/{uid} heartbeat for message-center online dots
  usePresenceHeartbeat();
  const hideChrome = CHROME_FREE_ROUTES.includes(location.pathname);

  useEffect(() => {
    if (!isDesktop) setMobileNavOpen(false);
  }, [location.pathname, isDesktop]);
  // Auth: lock viewport (those pages scroll internally). Chrome-free content
  // routes (/welcome, /complete-profile, /dev/icons): give main a definite
  // viewport height + overflow so the route's own scrollport (e.g.
  // OnboardingShell) can receive wheel/touch. Do NOT use height:auto +
  // overflow:visible here — App's flex chain + html overflow-x:hidden prevents
  // document scroll, which is why prior "unlock scroll" fixes still failed.
  // Logged-in chrome (sidebar/topbar): main MUST be the scrollport too —
  // overflow:hidden with a viewport-tall flex main clips/scrolls content away
  // while the fixed sidebar still paints (blank main sitewide).
  const lockMainViewport = AUTH_ROUTES.includes(location.pathname);
  const onMessages = location.pathname.startsWith('/messages');
  const chromeFreeScrollHost = hideChrome && !lockMainViewport;
  // Messages owns its scrollports (list + thread) — don't nest a page scroll on main
  const chromeScrollHost = Boolean(currentUser && !hideChrome && !onMessages);
  const mainIsScrollHost = lockMainViewport || chromeFreeScrollHost || chromeScrollHost || onMessages;
  // Resumability gate: a user mid-onboarding is pulled back to /welcome.
  // Never fire while profile I/O is in flight — a profile re-fetch (e.g. after
  // the session's first ID-token refresh) briefly nulls/re-loads userProfile,
  // and that transient state must never be read as "onboarding incomplete".
  const needsOnboarding =
    currentUser &&
    !profileCheckLoading &&
    shouldRouteToOnboarding(userProfile) &&
    !ONBOARDING_EXEMPT.includes(location.pathname);
  
  // Add scroll to top on route changes
  useMobileScrollToTop(['/messages']); // Exclude messages page to preserve scroll position in conversations
  
  // Initialize services when user changes
  useEffect(() => {
    if (currentUser) {
      // Initialize analytics and security services
      analyticsService.init(currentUser.uid);
      securityService.init(currentUser.uid);
      systemLogger.setUser(currentUser.uid);
      
      // Track successful login
      securityService.trackSuccessfulLogin(currentUser.uid, {
        source: 'app_init',
      });
    }
  }, [currentUser]);
  
  // Track page views
  useEffect(() => {
    if (currentUser && location.pathname) {
      analyticsService.trackPageView(location.pathname);
    }
  }, [location.pathname, currentUser]);

  // Route-aware document title + move focus to main after navigation
  useEffect(() => {
    document.title = documentTitleForPath(location.pathname);
    const main = mainRef.current;
    if (!main) return;
    // Keep the chrome scrollport at the top — focus must not leave a blank pane
    if (typeof main.scrollTo === 'function') {
      main.scrollTo(0, 0);
    } else {
      main.scrollTop = 0;
    }
    const focusMain = () => {
      try {
        main.focus({ preventScroll: true });
      } catch {
        main.focus();
      }
    };
    // Wait a tick so the new route's h1 (if any) is in the tree for SR context
    const id = window.requestAnimationFrame(focusMain);
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname]);
  
  // Prevent scrolling on login/register pages
  useEffect(() => {
    const isAuthPage = AUTH_ROUTES.includes(location.pathname);
    if (isAuthPage && !currentUser) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [location.pathname, currentUser]);

  // Resumability gate — pull a mid-flow user back into The Walkout.
  if (needsOnboarding) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <ErrorBoundary componentName="AppContent">
      <ProfilePictureProvider currentUser={currentUser} userProfile={userProfile}>
        <NotificationsProvider>
        <MuiLink
          href="#main-content"
          underline="none"
          sx={{
            position: 'absolute',
            left: -9999,
            top: 12,
            zIndex: 10000,
            px: 2,
            py: 1.25,
            minHeight: 48,
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: brandColors.brass,
            color: brandColors.void,
            fontFamily: '"Oswald", "Arial Narrow", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            border: `1px solid ${brandColors.brassBright}`,
            borderRadius: '2px',
            '&:focus': {
              left: 12,
              outline: `2px solid ${brandColors.brassBright}`,
              outlineOffset: 2,
            },
          }}
        >
          Skip to content
        </MuiLink>
        <ReconnectBanner />
        {currentUser && !hideChrome && (
          <Suspense fallback={null}>
            <CommandPalette />
          </Suspense>
        )}
        <Box sx={{ display: 'flex', minHeight: 'var(--vh-safe)', position: 'relative' }}>
          {/* Desktop: permanent rail + TopBar. Mobile: sticky TopBar (hamburger) + drawer. */}
          {currentUser && !hideChrome && (
            <ErrorBoundary componentName="Sidebar" minimal>
              {isDesktop ? (
                <Box sx={{
                  position: 'fixed',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '240px',
                  zIndex: 1200
                }}>
                  <Suspense fallback={<SidebarRailSkeleton />}>
                    <Sidebar />
                  </Suspense>
                </Box>
              ) : (
                <Suspense fallback={null}>
                  <Sidebar
                    open={mobileNavOpen}
                    onClose={() => setMobileNavOpen(false)}
                  />
                </Suspense>
              )}
            </ErrorBoundary>
          )}
          {currentUser && !hideChrome && (
            <ErrorBoundary componentName="TopBar" minimal>
              <Suspense fallback={null}>
                <TopBar
                  menuOpen={!isDesktop && mobileNavOpen}
                  onMenuClick={() => setMobileNavOpen(true)}
                />
              </Suspense>
            </ErrorBoundary>
          )}
          <Box
            component="main"
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            sx={{
              flexGrow: 1,
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              marginLeft: currentUser && isDesktop && !hideChrome ? '240px' : 0,
              paddingTop: currentUser && !hideChrome
                ? { xs: 'calc(64px + env(safe-area-inset-top, 0px))', lg: '64px' }
                : 0,
              paddingBottom: currentUser && !isDesktop && !hideChrome
                ? 'env(safe-area-inset-bottom, 0px)'
                : 0,
              minHeight: 'var(--vh-safe)',
              height: mainIsScrollHost ? 'var(--vh-safe)' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              // Auth / onboarding: overflow hidden so the route owns the scrollport.
              // Logged-in chrome: overflow auto so main scrolls (hidden clipped content
              // while the fixed sidebar still painted — sitewide blank main).
              overflowX: 'hidden',
              overflowY: lockMainViewport || chromeFreeScrollHost || onMessages
                ? 'hidden'
                : chromeScrollHost
                  ? 'auto'
                  : 'visible',
              // Landmark focus target for SPA route changes / skip-link — never
              // draw a page-frame outline (global :focus-visible is brass).
              outline: 'none',
              '&:focus, &:focus-visible': {
                outline: 'none',
              },
            }}
            className={`mobile-safe-bottom ${location.pathname === '/' && !currentUser ? 'landing-page-content' : ''}`}
          >
            <ErrorBoundary componentName="MainContent">
              {currentUser && !hideChrome && (
                <ErrorBoundary componentName="OnboardingBanner" minimal>
                  <OnboardingBanner />
                </ErrorBoundary>
              )}
              <ProfileCompletionGuard>
                <Suspense fallback={<RouteAwareFallback />}>
                  <Routes>
                    <Route
                      path="/welcome"
                      element={
                        <ErrorBoundary componentName="OnboardingFlow">
                          <OnboardingFlow />
                        </ErrorBoundary>
                      }
                    />
                    {process.env.NODE_ENV === 'development' && (
                      <Route
                        path="/dev/icons"
                        element={
                          <ErrorBoundary componentName="DevIcons">
                            <DevIcons />
                          </ErrorBoundary>
                        }
                      />
                    )}
                    <Route
                      path="/"
                      element={currentUser ? <Navigate to="/dashboard" /> : (
                        <ErrorBoundary componentName="LandingPage">
                          <LandingPageSimple />
                        </ErrorBoundary>
                      )}
                    />
                    <Route 
                      path="/dashboard"
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="Dashboard">
                            <Dashboard />
                          </ErrorBoundary>
                        </PrivateRoute>
                      }
                    />
                    <Route path="/login" element={
                      currentUser ? (
                        <Navigate
                          to={
                            typeof location.state?.from === 'string' &&
                            location.state.from.startsWith('/') &&
                            !location.state.from.startsWith('//')
                              ? location.state.from
                              : '/dashboard'
                          }
                          replace
                        />
                      ) : (
                        <ErrorBoundary componentName="Login">
                          <Login />
                        </ErrorBoundary>
                      )
                    } />
                    <Route path="/register" element={
                      currentUser ? <Navigate to="/dashboard" replace /> : (
                        <ErrorBoundary componentName="Register">
                          <Register />
                        </ErrorBoundary>
                      )
                    } />
                    <Route path="/forgot-password" element={
                      currentUser ? <Navigate to="/dashboard" replace /> : (
                        <ErrorBoundary componentName="ForgotPassword">
                          <ForgotPassword />
                        </ErrorBoundary>
                      )
                    } />
                    <Route path="/complete-profile" element={
                      <ErrorBoundary componentName="ProfileCompletionFlow">
                        <ProfileCompletionFlow />
                      </ErrorBoundary>
                    } />
                    {/* Marketplace Phase 1 — classifieds (no on-site checkout) */}
                    <Route path="/marketplace" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="AvailableListingsPage">
                          <AvailableListingsPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/marketplace/listing/:listingId" element={<Navigate to="/my-listings" replace />} />
                    <Route path="/marketplace/seller-dashboard" element={<Navigate to="/my-listings" replace />} />
                    <Route path="/my-listings" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="MyListingsPage">
                          <MyListingsPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    {/* Account / security settings (email, password, delete) */}
                    <Route path="/profile" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="ProfileSettingsPage">
                          <ProfileSettingsPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    {/* My Profile — public-facing surface for the signed-in user */}
                    <Route path="/me" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="EnhancedUserProfile">
                          <EnhancedUserProfile />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/users/me" element={<Navigate to="/me" replace />} />
                    <Route path="/public-profile" element={<Navigate to="/me" replace />} />
                    {/* Wave 13A — all profile deep links auth-gated (Clubhouse inline gate) */}
                    <Route path="/profile/:userId" element={
                      <PrivateRoute inlineGate>
                        <ErrorBoundary componentName="EnhancedUserProfile">
                          <EnhancedUserProfile />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/user/:userId" element={
                      <PrivateRoute inlineGate>
                        <ErrorBoundary componentName="EnhancedUserProfile">
                          <EnhancedUserProfile />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/u/:username" element={
                      <PrivateRoute inlineGate>
                        <ErrorBoundary componentName="EnhancedUserProfile">
                          <EnhancedUserProfile />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/users" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="UserDiscoveryPage">
                          <UserDiscoveryPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/users/search" element={
                      <ErrorBoundary componentName="UserSearchPage">
                        <UserSearchPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/users/:userId" element={
                      <PrivateRoute inlineGate>
                        <ErrorBoundary componentName="EnhancedUserProfile">
                          <EnhancedUserProfile />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route
                      path="/browse-fighters"
                      element={
                        <ErrorBoundary componentName="BrowseCardsByFighter">
                          <BrowseCardsByFighter />
                        </ErrorBoundary>
                      }
                    />
                    <Route 
                      path="/fighter/:fighterId" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="FighterDetailPage">
                            <FighterDetailPage />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route path="/logout" element={
                      <ErrorBoundary componentName="Logout">
                        <Logout />
                      </ErrorBoundary>
                    } />
                    <Route path="/my-collection" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="MyCollection">
                          <MyCollection />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/sets/:setId/cards/:cardId" element={
                      <ErrorBoundary componentName="CardDetailPage">
                        <CardDetailPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/sets/:setId/insert-sets/:insertSetId/cards/:cardId" element={
                      <ErrorBoundary componentName="CardDetailPage">
                        <CardDetailPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/sets/:setId/parallel-sets/:parallelSetId/cards/:cardId" element={
                      <ErrorBoundary componentName="CardDetailPage">
                        <CardDetailPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/sets/:setId/insert-sets/:insertSetId/parallels/:parallelSetId/cards/:cardId" element={
                      <ErrorBoundary componentName="CardDetailPage">
                        <CardDetailPage />
                      </ErrorBoundary>
                    } />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="AdminPage">
                            <AdminPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/vault-preview" element={<Navigate to="/" replace />} />
                    {/* TEMP: bulk TCDB image import — delete with BulkImageImportPage.js */}
                    <Route path="/admin/bulk-image-import" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="BulkImageImportPage">
                          <BulkImageImportPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: backfill missing base-set parallels — delete with BackfillParallelsPage.js */}
                    <Route path="/admin/backfill-parallels" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="BackfillParallelsPage">
                          <BackfillParallelsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: ingest 2010 Leaf MMA base set — delete with LeafMmaImportPage.js */}
                    <Route path="/admin/import-leaf-mma" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="LeafMmaImportPage">
                          <LeafMmaImportPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: ingest 2010 Leaf MMA autograph insert sets — delete with LeafMmaInsertsPage.js */}
                    <Route path="/admin/import-leaf-inserts" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="LeafMmaInsertsPage">
                          <LeafMmaInsertsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: import 2010 Leaf MMA scans — delete with LeafMmaImagesPage.js */}
                    <Route path="/admin/import-leaf-mma-images" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="LeafMmaImagesPage">
                          <LeafMmaImagesPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: clean fabricated Round 1 autographs — delete with CleanR1AutographsPage.js */}
                    <Route path="/admin/clean-r1-autographs" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="CleanR1AutographsPage">
                          <CleanR1AutographsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: rebuild Round 1 autographs to confirmed checklist — delete with FixR1AutographsPage.js */}
                    <Route path="/admin/fix-r1-autographs" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="FixR1AutographsPage">
                          <FixR1AutographsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: repair Round 1 cross-wired parallels — delete with FixR1ParallelsPage.js */}
                    <Route path="/admin/fix-r1-parallels" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="FixR1ParallelsPage">
                          <FixR1ParallelsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* PERMANENT: set data integrity audit */}
                    <Route path="/admin/audit-sets" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="AuditSetsPage">
                          <AuditSetsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/fighter-manager" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="FighterManagerPage">
                          <FighterManagerPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: reconcile legacy card-doc schemes — delete with ReconcileSetPage.js */}
                    <Route path="/admin/reconcile-set" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="ReconcileSetPage">
                          <ReconcileSetPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: import scraped TCDB sets — delete with ImportScrapedSetPage.js */}
                    <Route path="/admin/import-scraped-set" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="ImportScrapedSetPage">
                          <ImportScrapedSetPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: import scraped-set scans — delete with ImportScrapedImagesPage.js */}
                    <Route path="/admin/import-scraped-images" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="ImportScrapedImagesPage">
                          <ImportScrapedImagesPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: normalize landscape card images — delete with NormalizeOrientationPage.js */}
                    <Route path="/admin/normalize-orientation" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="NormalizeOrientationPage">
                          <NormalizeOrientationPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* TEMP: reconcile duplicate card-ID schemes — delete with ReconcileCardsPage.js */}
                    <Route path="/admin/reconcile-cards" element={
                      <ProtectedRoute>
                        <ErrorBoundary componentName="ReconcileCardsPage">
                          <ReconcileCardsPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route 
                      path="/add-card" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="AddCardPage">
                            <AddCardPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/add-fighter" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="AddFighterForm">
                            <AddFighterForm />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/edit-fighters" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="EditFightersPage">
                            <EditFightersPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/edit-cards" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="EditCardsPage">
                            <EditCardsPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/add-set" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="AddSetPage">
                            <AddSetPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/edit-sets" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="EditSetsPage">
                            <EditSetsPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/fighter-images" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="FighterImageAdminPage">
                            <FighterImageAdminPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/card-images" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="CardImageAdminPage">
                            <CardImageAdminPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/admin/image-approvals"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="ImageApprovalsPage">
                            <ImageApprovalsPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/admin/bulk-cards" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="BulkCardManagerPage">
                            <BulkCardManagerPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/bulk-card-updater" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="BulkCardUpdaterPage">
                            <BulkCardUpdaterPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/bulk-sets" 
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="BulkSetManagerPage">
                            <BulkSetManagerPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/admin/icons"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary componentName="IconsAdminPage">
                            <IconsAdminPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/sets" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="ViewSetsComponent">
                            <ViewSetsComponent />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/sets/:setId" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="SetDetails">
                            <SetDetails />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/wishlist" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="WishlistPage">
                            <WishlistPage />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/favorites" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="FavoritesPage">
                            <FavoritesPage />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/achievements" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="AchievementsPage">
                            <AchievementsPage />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/rainbow-tracker" 
                      element={
                        <PrivateRoute>
                          <ErrorBoundary componentName="RainbowTracker">
                            <RainbowTracker />
                          </ErrorBoundary>
                        </PrivateRoute>
                      } 
                    />
                    {/* Classifieds entry — list from card detail; sell-card redirects */}
                    <Route path="/sell-card" element={<Navigate to="/my-listings" replace />} />
                    <Route path="/messages" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="MessagesPage">
                          <MessagesPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    {/* CF chat_message actionUrl writes /messages/:id — same page + deep-link */}
                    <Route path="/messages/:conversationId" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="MessagesPage">
                          <MessagesPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/notifications" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="NotificationsPage">
                          <NotificationsPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/support" element={
                      <ErrorBoundary componentName="SupportPage">
                        <SupportPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/contact" element={
                      <Navigate to="/support" replace />
                    } />
                    <Route path="/settings/privacy" element={
                      <PrivateRoute>
                        <ErrorBoundary componentName="PrivacySettingsPage">
                          <PrivacySettingsPage />
                        </ErrorBoundary>
                      </PrivateRoute>
                    } />
                    <Route path="/privacy" element={
                      <ErrorBoundary componentName="PrivacyPage">
                        <PrivacyPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/terms" element={
                      <ErrorBoundary componentName="TermsPage">
                        <TermsPage />
                      </ErrorBoundary>
                    } />
                    {/* Catch-all 404 — must stay last */}
                    <Route path="*" element={
                      <ErrorBoundary componentName="NotFoundPage">
                        <NotFoundPage />
                      </ErrorBoundary>
                    } />
                  </Routes>
                </Suspense>
              </ProfileCompletionGuard>
            </ErrorBoundary>
            {!hideChrome && <BuildIdentity />}
          </Box>
        </Box>
        </NotificationsProvider>
      </ProfilePictureProvider>
    </ErrorBoundary>
  );
};

const App = () => {
  return (
    <ErrorBoundary componentName="App">
      <ThemeProvider theme={theme}>
        <MotionConfig reducedMotion="user">
          <CssBaseline />
          <ToastProvider>
            <AuthProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AppContent />
              </Router>
            </AuthProvider>
          </ToastProvider>
        </MotionConfig>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
